/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react"

import codeIconSrc from "@/assets/home-new/search/code.svg"
import semanticIconSrc from "@/assets/home-new/search/semantic.svg"
import grepIconSrc from "@/assets/home-new/search/tool-grep.svg"
import logIconSrc from "@/assets/home-new/search/tool-log.svg"
import readIconSrc from "@/assets/home-new/search/tool-read.svg"
import searchIconSrc from "@/assets/home-new/search/tool-search.svg"
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { agents } from "@/lib/agents"

import { cardRingClassName, elevatedRingClassName } from "../homeNewStyles"
import { useSectionInView } from "../sectionInView"
import { teamAvatars } from "../teamAvatars"
import { useRevealOnce } from "../useRevealOnce"

// Claim-card illustrations for the Agentic Search section of the new homepage.

const CARD_GAP_PX = 16
const LINK_HEIGHT_PX = 26

/**
 * The static dashed fork from the question down to both result cards, drawn
 * to the measured width so it lands on each card's center at any size.
 */
function SearchFork() {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(418)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const measure = () => setWidth(element.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const center = width / 2
  const left = (width - CARD_GAP_PX) / 4
  const right = width - left
  const mid = LINK_HEIGHT_PX / 2
  const radius = 8

  return (
    <div ref={ref} data-search-step="" style={{ "--step": 1 } as CSSProperties}>
      <svg
        width={width}
        height={LINK_HEIGHT_PX}
        viewBox={`0 0 ${width} ${LINK_HEIGHT_PX}`}
        fill="none"
        className="block overflow-visible text-(--neutral-250) dark:text-(--neutral-400)"
      >
        <path
          d={`M ${center} 0 V ${mid}`}
          stroke="currentColor"
          strokeDasharray="4 4"
          strokeLinejoin="round"
        />
        <path
          d={[
            `M ${left} ${LINK_HEIGHT_PX}`,
            `V ${mid + radius}`,
            `Q ${left} ${mid} ${left + radius} ${mid}`,
            `H ${right - radius}`,
            `Q ${right} ${mid} ${right} ${mid + radius}`,
            `V ${LINK_HEIGHT_PX}`,
          ].join(" ")}
          stroke="currentColor"
          strokeDasharray="4 4"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

function EngineLabel({ iconSrc, children }: { iconSrc: string; children: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5">
      <p className="min-w-0 flex-1 font-sans text-sm leading-4 font-semibold text-default">
        {children}
      </p>
      <img src={iconSrc} alt="" width={16} height={16} className="size-4 shrink-0" />
    </div>
  )
}

/**
 * "Code and context together": one question goes to both engines. Code search
 * finds the exact call site by symbol; semantic search finds the session that
 * explains it. The two answers sit side by side under the question.
 */
export function CodeAndContextIllustration() {
  const [revealed, ref] = useRevealOnce()
  const claude = agents.claude

  return (
    <div
      ref={ref}
      data-revealed={revealed ? "true" : "false"}
      className="home-new-search mx-auto flex w-full max-w-[418px] flex-col font-sans"
      aria-hidden="true"
    >
      {/* The question. */}
      <div
        data-search-step=""
        style={{ "--step": 0 } as CSSProperties}
        className={`flex items-center gap-2 rounded-lg bg-floating px-3 py-2 ${elevatedRingClassName}`}
      >
        <img
          src={teamAvatars.alisha}
          alt=""
          width={24}
          height={24}
          loading="lazy"
          className="size-6 shrink-0 rounded-full object-cover"
        />
        <p className="min-w-0 truncate text-sm leading-5 font-semibold tracking-[-0.01em] text-default">
          Why do we retry refreshToken twice?
        </p>
      </div>

      <SearchFork />

      <div className="grid grid-cols-2 gap-4">
        {/* Code search: the exact call site, matched by symbol. */}
        <div
          data-search-step=""
          style={{ "--step": 2 } as CSSProperties}
          className={`flex min-h-[178px] min-w-0 flex-col rounded-lg bg-(--neutral-150) py-1 ${cardRingClassName}`}
        >
          <EngineLabel iconSrc={codeIconSrc}>Code</EngineLabel>
          <div className="flex min-w-0 flex-col gap-1 px-3 py-1.5 font-mono text-xs leading-4 text-muted">
            <p>
              retry(
              <span className="rounded-[4px] bg-(--neutral-200) px-0.5 text-default">
                refreshToken
              </span>
              , 2)
            </p>
            <p>
              auth/
              <wbr />
              session.ts:118
            </p>
          </div>
        </div>

        {/* Semantic search: the session that explains why. */}
        <div
          data-search-step=""
          style={{ "--step": 3 } as CSSProperties}
          className={`flex min-h-[178px] min-w-0 flex-col rounded-lg bg-floating py-1 ${elevatedRingClassName}`}
        >
          <EngineLabel iconSrc={semanticIconSrc}>Semantic</EngineLabel>
          <p className="flex-1 px-3 py-1.5 text-sm leading-5 tracking-[-0.01em] text-default">
            “Upstream IdP drops ~2% of first refreshes; a second retry catches the rest.”
          </p>
          <div className="flex items-center gap-1 px-3 py-1.5">
            <span className="flex size-6 shrink-0 items-center justify-center">
              <span className="flex size-[18px] items-center justify-center rounded-full border border-(--neutral-250) bg-floating">
                <img
                  src={claude.logoUrl}
                  alt=""
                  width={12}
                  height={12}
                  className={`size-3 ${claude.invertOnDark ? "dark:invert" : ""}`}
                />
              </span>
            </span>
            <p className="min-w-0 truncate text-xs leading-5 font-medium text-muted">
              6w ago · 9f43c2a
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// "Fewer steps, fewer tokens": the benchmark's "why is our code like this?"
// question, answered twice. First the agent digs through history the long
// way (grep, read, log, one call at a time); the calls get struck out and
// cleared, last first, and the same question is answered with one search and
// two reads. After a pause it plays again from the top.

interface ToolCall {
  tool: string
  iconSrc: string
  target: string
}

const DIGGING: ToolCall[] = [
  { tool: "grep", iconSrc: grepIconSrc, target: '"RateLimit" -r internal/' },
  { tool: "read", iconSrc: readIconSrc, target: "internal/limiter/limiter.go" },
  { tool: "grep", iconSrc: grepIconSrc, target: '"inputs" internal/limiter' },
  { tool: "read", iconSrc: readIconSrc, target: "internal/limiter/bucket.go" },
  { tool: "log", iconSrc: logIconSrc, target: "-S countInputs" },
  { tool: "read", iconSrc: readIconSrc, target: "CHANGELOG.md" },
]

const SEARCHING: ToolCall[] = [
  { tool: "search", iconSrc: searchIconSrc, target: "why the limiter counts inputs" },
  { tool: "read", iconSrc: readIconSrc, target: "session 4c1e9a0" },
  { tool: "read", iconSrc: readIconSrc, target: "internal/limiter/bucket.go" },
]

// When each step starts, in ms from the start of a run. After the answer has
// been up for a while the search list lifts away and the run starts over.
const FEWER_STEPS = { strike: 1700, clear: 2900, search: 3800, reset: 8800, restart: 9600 } as const

type FewerStepsPhase = "idle" | "digging" | "struck" | "cleared" | "searching" | "resetting"

function ToolCallRow({
  call,
  index,
  primary = false,
}: {
  call: ToolCall
  index: number
  primary?: boolean
}) {
  return (
    <div
      data-tool-row=""
      style={{ "--row": index } as CSSProperties}
      className="flex h-6 items-center gap-3"
    >
      <span
        className={`flex shrink-0 items-center rounded-full p-1 ${
          primary
            ? `bg-floating ${elevatedRingClassName}`
            : `bg-(--neutral-100) ${cardRingClassName}`
        }`}
      >
        <img src={call.iconSrc} alt="" width={16} height={16} className="size-4" />
      </span>
      <div className="relative flex min-w-0 flex-1 items-center gap-2 px-3 font-mono text-xs leading-4">
        <span className={`shrink-0 ${primary ? "home-new-steps-primary" : "text-muted"}`}>
          {call.tool}
        </span>
        <span className="min-w-0 flex-1 truncate text-right text-muted">{call.target}</span>
        <span className="home-new-steps-strike" aria-hidden="true" />
      </div>
    </div>
  )
}

export function FewerStepsIllustration() {
  const [started, setStarted] = useState(false)
  const [phase, setPhase] = useState<FewerStepsPhase>("idle")
  const start = useCallback(() => {
    setStarted(true)
    setPhase("digging")
  }, [])
  const ref = useIntersectionObserver(start, {
    enabled: !started,
    rootMargin: "0px 0px -15% 0px",
  })

  // Once started, schedule the strike, the clear-out and the search list. The
  // cycle stops while the section is offscreen and starts over on return.
  const inView = useSectionInView()
  const prefersReducedMotion = usePrefersReducedMotion()
  useEffect(() => {
    if (!started || !inView) return
    if (prefersReducedMotion) {
      const frame = window.requestAnimationFrame(() => setPhase("searching"))
      return () => window.cancelAnimationFrame(frame)
    }
    const timers: number[] = []
    const run = () => {
      setPhase("digging")
      timers.push(
        window.setTimeout(() => setPhase("struck"), FEWER_STEPS.strike),
        window.setTimeout(() => setPhase("cleared"), FEWER_STEPS.clear),
        window.setTimeout(() => setPhase("searching"), FEWER_STEPS.search),
        window.setTimeout(() => setPhase("resetting"), FEWER_STEPS.reset),
        window.setTimeout(run, FEWER_STEPS.restart),
      )
    }
    run()
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [started, inView, prefersReducedMotion])

  const codex = agents.codex

  return (
    <div
      ref={ref}
      data-phase={phase}
      className="home-new-steps mx-auto grid w-full max-w-[418px] font-sans"
      aria-hidden="true"
    >
      {/* The long way round: one call at a time through the history. */}
      <div data-steps-list="digging" className="flex flex-col gap-2 [grid-area:1/1]">
        {DIGGING.map((call, index) => (
          <ToolCallRow key={index} call={call} index={index} />
        ))}
      </div>

      {/* With search: one query, two reads, and the answer. */}
      <div data-steps-list="searching" className="flex flex-col gap-3 [grid-area:1/1]">
        <div className="flex flex-col gap-2">
          {SEARCHING.map((call, index) => (
            <ToolCallRow key={index} call={call} index={index} primary={index === 0} />
          ))}
        </div>
        <div
          data-tool-row=""
          style={{ "--row": SEARCHING.length } as CSSProperties}
          className="flex gap-3"
        >
          <span
            className={`flex size-6 shrink-0 items-center justify-center rounded-full bg-floating ${cardRingClassName}`}
          >
            <img
              src={codex.logoUrl}
              alt=""
              width={16}
              height={16}
              className={`size-4 ${codex.invertOnDark ? "dark:invert" : ""}`}
            />
          </span>
          <p
            className={`min-w-0 flex-1 rounded-lg bg-floating px-3 py-2 text-sm leading-6 text-default ${elevatedRingClassName}`}
          >
            Batched calls let one request fan out into hundreds of writes, so the limiter switched
            to counting inputs.
          </p>
        </div>
      </div>
    </div>
  )
}
